<template>
  <ul class="flex flex-wrap gap-2" data-testid="filter-group">
    <template v-if="(showAll && allFilters.length) || (!showAll && addedFilters.length)">
      <li
        v-for="f in allFilters"
        :key="f.label"
        v-tippy="{
          content: selectedValuesTooltipMap[f.name],
        }"
        class="overflow-hidden"
      >
        <filter-item
          v-if="isAddedFilterObject(f) && f.addedFilter"
          ref="filterRef"
          :filter-id="f.name"
          :filter-label="f.label"
          :show-add-similar-button="f.showAddSimilarButton"
          :description="f.description"
          :conditions="f.conditions"
          :selected-condition="f.selectedCondition"
          :value="f.value"
          :value-type="f.valueType"
          :value-component-props="f.valueComponentProps"
          :intelligence-icon="f.intelligenceIcon"
          :bot-icon="f.botIcon"
          :force-show-remove-button="!showAll"
          :force-hide-remove-button="f.forceHideRemoveButton"
          :status="f.status"
          :is-spotlight="f.isSpotlight"
          :disabled="!!f.disabled"
          @remove="$emit('remove', f.id)"
          @filter-applied="filterApplied(f.id, $event)"
          @add-similar-filter="addSimilarFilter($event, f.name)"
        />
        <button
          v-else
          v-tippy="{ content: !hideDescriptionTooltip(f) ? f.description : undefined }"
          :data-testid="`filter-item-${f.name}`"
          class="hover:bg-gray-30 group flex h-7 max-w-full cursor-pointer items-center rounded-md px-3 duration-200 ease-out"
          :class="f.isIntelligent ? $style.holo : 'bg-gray-20'"
          type="button"
          @click.prevent="!isAddedFilterObject(f) && filterAddingStarted(f)"
        >
          <span
            class="text-gray-60 group-hover:text-gray-70 flex items-center overflow-hidden whitespace-nowrap text-xs font-medium duration-200 ease-out"
          >
            <intelligence-icon v-if="f.intelligenceIcon" class="mr-2 h-3.5 shrink-0" />
            <bot-icon v-else-if="f.botIcon" class="mr-2 h-4 shrink-0" />
            <span class="truncate">
              {{ f.label }}
            </span>
          </span>
        </button>
      </li>
    </template>
    <li v-else-if="showAll && !allFilters.length && searchFilter?.length" class="text-sm font-medium text-gray-50">
      {{ $t('universal.no_items_found') }}
    </li>
    <li v-if="showAddButton">
      <ui-dropdown
        ref="dropdownRef"
        :mode="Modes.Single"
        close-on-select
        :model-value="[]"
        :max-width="999"
        :disabled="false"
        :options="filterOptions"
        :search-placeholder="$t('universal.search')"
        :no-results-text="$t('universal.no_items_found')"
        has-search
        size="lg"
        :filter-results="!hasPagination"
        :select-all-mode="SelectAllModes.None"
        :is-object="true"
        :total="total"
        label="label"
        prop-to-check="name"
        class="group outline-0"
        append-to-document
        @search-change="handleSearchChange"
        @update:model-value="filterAddingStarted($event)"
      >
        <template #trigger>
          <ui-menu-filter-trigger :show-chevron="false" :full-width="false" size="md" :error="error">
            <template #icon>
              <plus-icon class="icon-m" />
            </template>
          </ui-menu-filter-trigger>
        </template>
        <template #option="{ option }">
          <div v-if="option.name === 'placeholder'" class="text-gray-60 pointer-events-none text-xs">
            {{ option.label }}
          </div>
          <div v-else-if="option.intelligenceIcon" class="flex items-center gap-2">
            {{ option.label }}
            <intelligence-icon class="icon-s shrink-0" />
          </div>
        </template>
        <template #more-count="{ count }">
          <button v-if="!searchQuery" type="button" class="hover:text-gray-60" @click="$emit('load-more')">
            {{ $t('universal.n_more_simple', { n: $n(count) }, count) }}
          </button>
        </template>
      </ui-dropdown>
    </li>
  </ul>
</template>

<script lang="ts" setup>
import { PlusIcon } from '@klausapp/ui-kit/icons'
import { UiDropdownV2 as UiDropdown, UiMenuFilterTrigger } from '@klausapp/ui-kit'
import { Modes, SelectAllModes } from '@klausapp/ui-kit/components/UiDropdownV2/types'
import { computed, nextTick, ref } from 'vue'
import { matchSorter } from 'match-sorter'
import IntelligenceIcon from '@/assets/intelligence-icon.svg'
import i18n from '@/i18n'
import BotIcon from '@/assets/bot.svg'
import { getTicketStatusLabel } from '@/modules/shared/TicketContent/utils/ticketStatus.util'
import FilterItem from './FilterItem.vue'
import type { FilterItemEmit, FilterOption, AddedFilter, DropdownSelectedValue } from './types'

const props = defineProps<{
  addedFilters: AddedFilter[]
  filterOptions: FilterOption[]
  showAll?: boolean
  searchFilter?: string
  hasPagination?: boolean
  total?: number
  error?: boolean
}>()

const emit = defineEmits<{
  (e: 'filter-adding-started', o: FilterOption): void
  (e: 'filter-applied', id: string, f: FilterItemEmit): void
  (e: 'add-similar-filter', f: FilterItemEmit, o: FilterOption): void
  (e: 'remove', f: string): void
  (e: 'search-change', search: string): void
  (e: 'load-more'): void
}>()

const filterRef = ref()
const dropdownRef = ref()
const showDropdown = () => dropdownRef.value.show()
const searchQuery = ref('')

const allFilters = computed<(FilterOption | AddedFilter)[]>(() => {
  const addedFilters = props.addedFilters.map((f) => ({ ...f, addedFilter: true }))

  if (props.showAll) {
    const filterOptions = props.filterOptions.filter((f) => !props.addedFilters.find(({ name }) => name === f.name))
    const allOptions = [...addedFilters, ...filterOptions].sort((a, b) => a.label.localeCompare(b.label))
    const filteredOptions = props.searchFilter?.length
      ? matchSorter(allOptions, props.searchFilter, { keys: ['label'] })
      : allOptions
    return filteredOptions
  }

  return addedFilters
})

const filterAddingStarted = async (o: FilterOption) => {
  emit('filter-adding-started', o)
  await nextTick()
  if (!filterRef.value) return

  const i = filterRef.value.length - 1
  filterRef.value[i]?.$el.click()
}

const filterApplied = (id: string, f: FilterItemEmit) => {
  emit('filter-applied', id, f)
}

const addSimilarFilter = (f: FilterItemEmit, filterName: string) => {
  const o = props.filterOptions.find((f) => f.name === filterName)
  emit('add-similar-filter', f, o)
}

const isAddedFilterObject = (obj: unknown): obj is AddedFilter => {
  return typeof obj === 'object' && obj !== null && 'valueType' in obj
}

const handleSearchChange = (search: string) => {
  searchQuery.value = search
  emit('search-change', search)
}

const showAddButton = computed(() => {
  return (props.filterOptions.length && !props.showAll) || props.hasPagination
})

const hideDescriptionTooltip = (f: FilterOption | AddedFilter) => 'isSpotlight' in f && f.isSpotlight

const selectedValuesTooltip = (f: FilterOption | AddedFilter) => {
  if (!isAddedFilterObject(f)) return null

  if (f.name === 'TICKET_STATUS' && !!f.disabled && (f?.value as DropdownSelectedValue[])?.length > 1) {
    return i18n.t('settings.ai.conversations_filter_modal.multiple_disabled_selected', {
      options: (f.value as DropdownSelectedValue[]).map((v) => getTicketStatusLabel(v.label)).join(', '),
    })
  }

  return null
}

const selectedValuesTooltipMap = computed(() =>
  allFilters.value.reduce<Record<string, string | null>>((acc, f) => {
    acc[f.name] = selectedValuesTooltip(f)
    return acc
  }, {}),
)

defineExpose({ showDropdown })
</script>

<style lang="scss" module>
.holo {
  position: relative;
  color: var(--gray-70);
  z-index: 1;

  &:not(:disabled) {
    &::before,
    &::after {
      content: '';
      position: absolute;
      z-index: -1;
    }

    &::before {
      inset: 0;
      border-radius: var(--border-radius);
      background: linear-gradient(
        42deg,
        var(--blue-30, #9de4fc) 14.47%,
        var(--yellow-30, #faeb89) 37.87%,
        var(--pink-30, #febbd8) 61.27%,
        var(--brand-30, #b3b2f9) 87.59%
      );
    }

    &:after {
      inset: 1px;
      border-radius: calc(var(--border-radius) - 1px);
      background:
        linear-gradient(
          305deg,
          rgba(255, 195, 255, 0.25) 11.07%,
          rgba(253, 231, 174, 0.25) 55.34%,
          rgba(255, 221, 253, 0.25) 99.61%
        ),
        white;
    }
  }
}
</style>
